"use server"

import { sdk } from "@lib/config"
import medusaError from "@lib/util/medusa-error"
import { getAuthHeaders, getCacheOptions } from "./cookies"
import { HttpTypes } from "@medusajs/types"

export const retrieveOrder = async (id: string) => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("orders")),
  }

  return sdk.client
    .fetch<HttpTypes.StoreOrderResponse>(`/store/orders/${id}`, {
      method: "GET",
      query: {
        fields:
          "*payment_collections.payments,*items,*items.metadata,*items.variant,*items.product",
      },
      headers,
      next,
      cache: "force-cache",
    })
    .then(({ order }) => order)
    .catch((err) => medusaError(err))
}

export const listOrders = async (
  limit: number = 10,
  offset: number = 0,
  filters?: Record<string, any>
) => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("orders")),
  }

  return sdk.client
    .fetch<HttpTypes.StoreOrderListResponse>(`/store/orders`, {
      method: "GET",
      query: {
        limit,
        offset,
        order: "-created_at",
        fields: "*items,+items.metadata,*items.variant,*items.product",
        ...filters,
      },
      headers,
      next,
      cache: "force-cache",
    })
    .then(({ orders }) => orders)
    .catch((err) => medusaError(err))
}

export const updateOrderShippingAddress = async (
  orderId: string,
  data: {
    first_name: string
    last_name: string
    address_1: string
    address_2?: string
    city: string
    province?: string
    postal_code: string
  }
): Promise<{ success: boolean; error: string | null }> => {
  const headers = await getAuthHeaders()
  return sdk.client
    .fetch(`/store/orders/${orderId}/shipping-address`, {
      method: "PUT",
      body: data,
      headers,
    })
    .then(() => ({ success: true, error: null }))
    .catch((err) => ({ success: false, error: err.message ?? "Failed to update address" }))
}

export const createTransferRequest = async (
  state: {
    success: boolean
    error: string | null
    order: HttpTypes.StoreOrder | null
  },
  formData: FormData
): Promise<{
  success: boolean
  error: string | null
  order: HttpTypes.StoreOrder | null
}> => {
  const id = formData.get("order_id") as string

  if (!id) {
    return { success: false, error: "Order ID is required", order: null }
  }

  const headers = await getAuthHeaders()

  return await sdk.store.order
    .requestTransfer(
      id,
      {},
      {
        fields: "id, email",
      },
      headers
    )
    .then(({ order }) => ({ success: true, error: null, order }))
    .catch((err) => ({ success: false, error: err.message, order: null }))
}

export const acceptTransferRequest = async (id: string, token: string) => {
  const headers = await getAuthHeaders()

  return await sdk.store.order
    .acceptTransfer(id, { token }, {}, headers)
    .then(({ order }) => ({ success: true, error: null, order }))
    .catch((err) => ({ success: false, error: err.message, order: null }))
}

export const declineTransferRequest = async (id: string, token: string) => {
  const headers = await getAuthHeaders()

  return await sdk.store.order
    .declineTransfer(id, { token }, {}, headers)
    .then(({ order }) => ({ success: true, error: null, order }))
    .catch((err) => ({ success: false, error: err.message, order: null }))
}

export const findOrderByDisplayId = async (
  displayId: string,
  emailOrPostcode: string
): Promise<{
  found: boolean
  order: HttpTypes.StoreOrder | null
  error: string | null
  needsAuth: boolean
}> => {
  const headers = await getAuthHeaders()
  const isAuthenticated = Object.keys(headers).length > 0

  if (!isAuthenticated) {
    return { found: false, order: null, error: null, needsAuth: true }
  }

  const numericId = displayId.replace(/^#/, "").trim()

  return sdk.client
    .fetch<HttpTypes.StoreOrderListResponse>(`/store/orders`, {
      method: "GET",
      query: {
        display_id: numericId,
        fields: "*items,*items.variant,*items.product,*payment_collections.payments",
      },
      headers,
    })
    .then(({ orders }) => {
      if (!orders || orders.length === 0) {
        return { found: false, order: null, error: "Order not found. Please check your order number and email address.", needsAuth: false }
      }
      const matched = orders.find((o) => {
        const email = o.email?.toLowerCase() ?? ""
        const billing = o.billing_address?.postal_code?.toLowerCase() ?? ""
        const shipping = o.shipping_address?.postal_code?.toLowerCase() ?? ""
        const query = emailOrPostcode.toLowerCase().trim()
        return email === query || billing === query || shipping === query
      }) ?? orders[0]

      return { found: true, order: matched, error: null, needsAuth: false }
    })
    .catch(() => ({
      found: false,
      order: null,
      error: "Unable to look up your order right now. Please try again or contact us.",
      needsAuth: false,
    }))
}
